import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import type { Notification, NotificationSearchFilters } from '../types/notification';
import { 
  NOTIFICATION_CONSTANTS, 
  NOTIFICATION_UI_CONFIG, 
  NOTIFICATION_TYPE_DISPLAY_NAMES
} from '../constants/notifications';

/**
 * Get notifications with 3-day filtering for read notifications
 */
export const getUserNotifications = async (userId: string, filters: NotificationSearchFilters = {}, userEmail?: string): Promise<Notification[]> => {
  try {
    let q;

    // Sometimes backend stores userId as `${uid}_${sanitizedEmail}`.
    // If we have the user's email, query for both possible values using an 'in' filter.
    if (userEmail) {
      const sanitized = userEmail.replace(/[@.]/g, '_');
      const alt = `${userId}_${sanitized}`;
      q = query(
        collection(db, 'notifications'),
        where('userId', 'in', [userId, alt])
      );
    } else {
      q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId)
      );
    }
    
    // Apply additional filters
    if (filters.companyId) {
      q = query(q, where('companyId', '==', filters.companyId));
    }
    
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }
    
    if (filters.priority) {
      q = query(q, where('priority', '==', filters.priority));
    }
    
    // Order by creation date (newest first) and limit results
    q = query(q, orderBy('created_at', 'desc'), limit(NOTIFICATION_CONSTANTS.MAX_NOTIFICATIONS_PER_FETCH));
    
    const snapshot = await getDocs(q);
    
    const allNotifications = snapshot.docs.map(doc => {
      const data = doc.data();
      // Ensure isRead is properly set - if read_at exists, it's read
      const isRead = data.isRead === true || !!data.read_at;
      return {
        id: doc.id,
        ...data,
        isRead, // Explicitly set isRead based on read_at if not already set
        created_at: data.created_at?.toDate() || new Date(),
        sent_at: data.sent_at?.toDate() || data.created_at?.toDate() || new Date(), // Fallback to created_at if sent_at doesn't exist
        read_at: data.read_at?.toDate() || null,
        expires_at: data.expires_at?.toDate() || null,
      } as Notification;
    });

    console.log('🔔 [getUserNotifications] Fetched', allNotifications.length, 'raw notifications from Firestore');
    console.log('🔔 [getUserNotifications] Unread count:', allNotifications.filter(n => !n.isRead).length);
    console.log('🔔 [getUserNotifications] First few notifications:', allNotifications.slice(0, 3).map(n => ({
      id: n.id,
      type: n.type,
      isRead: n.isRead,
      read_at: n.read_at,
      created_at: n.created_at,
      title: n.title
    })));

    // Filter displayable notifications (all unread + today's read)
    return filterDisplayableNotifications(allNotifications);
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Filter notifications to display in the UI
 * Logic:
 * - Show ALL unread notifications (regardless of when sent)
 * - Show notifications sent today (based on sent_at date), even if read
 * - Hide notifications sent on past days that have been read
 */
export const filterDisplayableNotifications = (notifications: Notification[]): Notification[] => {
  // Get start and end of current date (for filtering sent notifications)
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  
  const filtered = notifications.filter(notification => {
    // Check if unread (check both isRead field and read_at)
    const isUnread = !notification.isRead && !notification.read_at;
    
    // Always show ALL unread notifications, regardless of when sent
    if (isUnread) {
      console.log('🔔 [filter] Showing unread notification:', notification.id, notification.title);
      return true;
    }
    
    // For read notifications, only show if SENT TODAY (based on sent_at)
    if (notification.sent_at) {
      const sentAt = notification.sent_at instanceof Date 
        ? notification.sent_at 
        : new Date(notification.sent_at);
      
      const wasSentToday = sentAt >= startOfToday && sentAt <= endOfToday;
      
      if (wasSentToday) {
        console.log('🔔 [filter] Showing notification sent today:', notification.id, 'sent:', sentAt);
        return true;
      } else {
        console.log('🔔 [filter] Hiding notification sent on past date:', notification.id, 'sent:', sentAt);
        return false;
      }
    }

    // If somehow no sent_at timestamp, hide it (shouldn't happen with new notifications)
    console.log('🔔 [filter] Hiding notification without sent_at:', notification.id);
    return false;
  });

  console.log('🔔 [filter] Filtered', filtered.length, 'of', notifications.length, 'notifications (all unread + today\'s sent)');
  return filtered;
};

/**
 * Get count of unread notifications
 */
export const getUnreadNotificationCount = (notifications: Notification[]): number => {
  return notifications.filter(n => !n.isRead).length;
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      isRead: true,
      read_at: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Get a single notification by ID
 */
export const getNotificationById = async (notificationId: string): Promise<Notification | null> => {
  try {
    const { getDoc } = await import('firebase/firestore');
    const docRef = doc(db, 'notifications', notificationId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    const isRead = data.isRead === true || !!data.read_at;
    return {
      id: snap.id,
      ...data,
      isRead,
      created_at: data.created_at?.toDate() || new Date(),
      read_at: data.read_at?.toDate() || null,
      expires_at: data.expires_at?.toDate() || null,
    } as Notification;
  } catch (error) {
    console.error('Error fetching notification by id:', error);
    throw error;
  }
};

    /**
     * Find a notification doc ID by a pushId value. Retry briefly to handle eventual consistency.
     */
    export const findNotificationDocIdByPushId = async (pushId: string, attempts = 3, delayMs = 500): Promise<string | null> => {
      try {
        for (let i = 0; i < attempts; i++) {
          // First try top-level field 'pushId'
          let q = query(collection(db, 'notifications'), where('pushId', '==', pushId), limit(1));
          let snap = await getDocs(q);
          if (!snap.empty) return snap.docs[0].id;

          // Then try nested field 'data.pushId'
          q = query(collection(db, 'notifications'), where('data.pushId', '==', pushId), limit(1));
          snap = await getDocs(q);
          if (!snap.empty) return snap.docs[0].id;

          // Try other common fields
          q = query(collection(db, 'notifications'), where('data.notificationId', '==', pushId), limit(1));
          snap = await getDocs(q);
          if (!snap.empty) return snap.docs[0].id;

          // Wait before next attempt
          if (i < attempts - 1) await new Promise(res => setTimeout(res, delayMs));
        }
        return null;
      } catch (error) {
        console.error('Error finding notification by pushId:', error);
        return null;
      }
    };

/**
 * Get priority color for UI display
 */
export const getPriorityColor = (priority: string): string => {
  return NOTIFICATION_UI_CONFIG.PRIORITY_COLORS[priority as keyof typeof NOTIFICATION_UI_CONFIG.PRIORITY_COLORS] || NOTIFICATION_UI_CONFIG.PRIORITY_COLORS.normal;
};

/**
 * Get notification type display name
 */
export const getNotificationTypeDisplay = (type: string): string => {
  return NOTIFICATION_TYPE_DISPLAY_NAMES[type as keyof typeof NOTIFICATION_TYPE_DISPLAY_NAMES] || type;
};
